/** Account finance comes only from current, explicitly present Rithmic fields. */
import { EXCHANGE_TIME_ZONE, formatTime } from "../time/zone.ts";
import { FIRM_RULE_LABELS, unavailableText, type BrokerFigure } from "./brokerRisk.ts";
import type { TargetRoom } from "./firmFigures.ts";
import type { ColumnDef, TableDef } from "./columns.ts";
export type AccountRiskFacts = {
  feesTodayCents: bigint;
  equityCents: bigint;
  ddThresholdCents: bigint | null;
  roomCents: bigint | null;
  dailyLossRoomCents: bigint | null;
  status: number;
  bindingConstraint: string;
  /** Prop-account figures (ADR-0034); null = not configured / not known. */
  prop?: AccountPropFacts;
  /** The gateway had a fresh market mark for every open position (audit C1): false = its limits
   * omit positions without fresh marks. */
  unrealizedTracked: boolean;
};

/** Where a figure came from (`RiskState.*_source`). */
export type PropSource = "override" | "rms" | "profile" | "operator" | "";

/** `RiskState` prop-account fields (ADR-0034), cents. */
export type AccountPropFacts = {
  dayPnlCents: bigint;
  startBalanceCents: bigint | null;
  profitTargetCents: bigint | null;
  profitTargetRoomCents: bigint | null;
  dailyLossLimitCents: bigint | null;
  pdllRoomCents: bigint | null;
  drawdownAmountCents: bigint | null;
  autoLiqLevelCents: bigint | null;
  autoLiqRoomCents: bigint | null;
  profitTargetSource: string;
  dailyLossSource: string;
  drawdownSource: string;
  autoLiqSource: string;
  profileLabel: string;
  profileVerified: boolean;
  profileBasis: string;
  profileCandidates: readonly string[];
};

export type AccountFacts = {
  accountId: string;
  label: string;
  isMain: boolean;
  status: "active" | "inactive" | "locked" | "unknown";
  simulation: boolean;
  /** null = unknown (no PnL plant money yet). */
  balanceCents: bigint | null;
  /**
   * Balance and today's realized P&L come from Rithmic's PnL plant (every fill on every platform,
   * ADR-0038); false/absent = unavailable.
   */
  pnlPlant?: boolean;
  realizedCents: bigint | null;
  unrealizedCents: bigint | null;
  openPositions: number | null;
  workingOrders: number | null;
  risk: AccountRiskFacts | null;
  dayPnlCents?: bigint | null;
  cashOnHandCents?: bigint | null;
  closedPositionPnlCents?: bigint | null;
  financeReason?: string;
  brokerCurrency?: string | null;
  /** When Rithmic set each money figure shown (ADR-0056); absent = not known. */
  asOf?: MoneyAsOf;
  /** Rithmic's own risk values and figures (ADR-0045); absent = unavailable. */
  broker?: AccountBrokerFacts;
};

export type AccountBrokerFacts = {
  threshold: { valueCents: bigint | null; reason: string; raw: string | null };
  trailingDrawdown: BrokerFigure;
  dailyLoss: BrokerFigure;
  /** HELM-calculated from the owner's firm rules (ADR-0047); absent = not computed. */
  targetRoom?: TargetRoom;
};

/**
 * `AccountUpdate.field_times` (ADR-0056): per money field, the broker time (451 ssboe/usecs, ns)
 * of the update that set the value. Rithmic sends only changed fields, so a value can be older
 * than the latest update; 0 = that update carried no broker time.
 */
export type MoneyFieldTimes = {
  balanceNs: bigint;
  realizedTodayNs: bigint;
  unrealizedNs: bigint;
  dayPnlNs: bigint;
  cashOnHandNs: bigint;
  closedPositionPnlNs: bigint;
};

/**
 * Per shown money figure, the broker time (ns) Rithmic set it: 0n when Rithmic gave no time
 * (or an older gateway sent none), null when the figure is not shown.
 */
export type MoneyAsOf = {
  balance: bigint | null;
  realized: bigint | null;
  unrealized: bigint | null;
  dayPnl: bigint | null;
  cashOnHand: bigint | null;
  closedPositionPnl: bigint | null;
};

export const NO_MONEY_AS_OF: MoneyAsOf = {
  balance: null,
  realized: null,
  unrealized: null,
  dayPnl: null,
  cashOnHand: null,
  closedPositionPnl: null,
};

/**
 * "Rithmic, as of 10:42:15 CT" (exchange time): when Rithmic set the figure shown, whether the
 * latest 451 carried it or it is the last value of the session. "" when no figure is shown.
 */
export function rithmicAsOf(ns: bigint | null | undefined): string {
  if (ns === null || ns === undefined) return "";
  if (ns <= 0n) return "Rithmic (no broker time reported)";
  return `Rithmic, as of ${formatTime(Number(ns / 1_000_000n), EXCHANGE_TIME_ZONE)} CT`;
}

export type AccountMoneyInputs = {
  connected: boolean;
  replayReady: boolean;
  unconfirmed: boolean;
  plant: {
    brokerCurrency?: string | undefined;
    brokerFinanceCurrent?: boolean;
    brokerFinanceReason?: string;
    balanceCents?: bigint | undefined;
    realizedTodayCents?: bigint | undefined;
    unrealizedCents?: bigint | undefined;
    dayPnlCents?: bigint | undefined;
    cashOnHandCents?: bigint | undefined;
    closedPositionPnlCents?: bigint | undefined;
    fieldTimes?: MoneyFieldTimes | undefined;
  } | null;
};

export function accountFinanceReason(m: AccountMoneyInputs): string {
  if (!m.connected) return "Gateway disconnected";
  if (!m.replayReady) return "Waiting for gateway replay to finish";
  if (m.unconfirmed) return "Broker account positions unconfirmed";
  if (!m.plant?.brokerFinanceCurrent)
    return m.plant?.brokerFinanceReason || "Current Rithmic account finance unavailable";
  if (!m.plant.brokerCurrency) return "Rithmic account currency unavailable";
  if (m.plant.brokerCurrency !== "USD")
    return "Rithmic currency denomination is not supported by the verified cent display";
  return "";
}

/** No fallback, summation or inferred meaning. A reported zero is a valid value. */
export function accountMoney(m: AccountMoneyInputs) {
  const financeReason = accountFinanceReason(m);
  const p = financeReason === "" ? m.plant : null;
  const balanceCents = p?.balanceCents ?? null;
  const realizedCents = p?.realizedTodayCents ?? null;
  const unrealizedCents = p?.unrealizedCents ?? null;
  const dayPnlCents = p?.dayPnlCents ?? null;
  const cashOnHandCents = p?.cashOnHandCents ?? null;
  const closedPositionPnlCents = p?.closedPositionPnlCents ?? null;
  const t = p?.fieldTimes;
  const at = (value: bigint | null, ns: bigint | undefined): bigint | null =>
    value === null ? null : (ns ?? 0n);
  const asOf: MoneyAsOf =
    p === null
      ? NO_MONEY_AS_OF
      : {
          balance: at(balanceCents, t?.balanceNs),
          realized: at(realizedCents, t?.realizedTodayNs),
          unrealized: at(unrealizedCents, t?.unrealizedNs),
          dayPnl: at(dayPnlCents, t?.dayPnlNs),
          cashOnHand: at(cashOnHandCents, t?.cashOnHandNs),
          closedPositionPnl: at(closedPositionPnlCents, t?.closedPositionPnlNs),
        };
  return {
    pnlPlant: p !== null,
    financeReason,
    brokerCurrency: m.plant?.brokerCurrency || null,
    balanceCents,
    realizedCents,
    unrealizedCents,
    dayPnlCents,
    cashOnHandCents,
    closedPositionPnlCents,
    asOf,
  };
}

export const BROKER_LIMIT_UNAVAILABLE =
  "Unavailable: verified current Rithmic account risk coverage has not been supplied. No preset, override or TradePrizm calculation is used.";
export function accountNetLiq(_a: AccountFacts): bigint | null {
  return null;
}
export function accountDayPnl(a: AccountFacts): bigint | null {
  return a.dayPnlCents ?? null;
}
export function accountDollarTotal(_a: AccountFacts): bigint | null {
  return null;
}
export function accountSinceStart(_a: AccountFacts): bigint | null {
  return null;
}
export function accountRiskBasis(_a: AccountFacts): string | null {
  return null;
}
export function distToDailyLoss(
  _a: AccountFacts,
): { roomCents: bigint; limitCents: bigint; which: "firm" | "pdll" } | null {
  return null;
}
export function distToAutoLiq(_a: AccountFacts): bigint | null {
  return null;
}
export function profitTargetProgressPct(_a: AccountFacts): number | null {
  return null;
}
/** Why an Accounts money cell is unknown (its tooltip): the account's finance reason, if any. */
export function moneyUnavailableTitle(financeReason: string | undefined): string {
  return financeReason || "Unavailable: this field was not reported by Rithmic.";
}

/**
 * Σ Account Balance over `accounts` by the Accounts Total row's rule (`subtotals`): exact cents,
 * unknown while any account's balance is unknown (or there is no account). `reason` says why it
 * is unknown ("" when known), with the first unknown account's Account Balance tooltip.
 */
export function balanceTotal(accounts: readonly { balanceCents: bigint | null; financeReason?: string }[]): {
  cents: bigint | null;
  reason: string;
} {
  if (accounts.length === 0) return { cents: null, reason: "Unavailable: no Rithmic account." };
  let cents = 0n;
  for (const a of accounts) {
    if (a.balanceCents === null) {
      const unknown = accounts.filter((x) => x.balanceCents === null).length;
      return {
        cents: null,
        reason: `Unknown while any account's balance is unknown (${String(unknown)} of ${String(accounts.length)}). ${moneyUnavailableTitle(a.financeReason)}`,
      };
    }
    cents += a.balanceCents;
  }
  return { cents, reason: "" };
}

export function moneySourceTitle(a: AccountFacts): string {
  return a.financeReason || "Direct Rithmic account fields only; absent fields are unavailable.";
}
/**
 * "Rithmic · unverified: Account balance − auto-liq threshold = … Why: …" (cell tooltip), with the
 * source note when there is one ("Differs from Rithmic by $X …", or why HELM's own figure is used).
 */
export function brokerFigureTitle(f: BrokerFigure): string {
  const note = f.note ? ` ${f.note}` : "";
  if (f.source === "unavailable")
    return `${unavailableText(f.reason)}. No TradePrizm, preset or override value is used.${note}`;
  const why = f.reason === "" ? "" : ` ${f.reason}`;
  return `${f.label}: ${f.formula?.label ?? ""} = ${f.formula?.expression ?? ""}.${why}${note}`;
}

/** The cell badge of a room figure: Rithmic's verification, or "helm" for HELM's own figure. */
export function figureBadge(f: BrokerFigure | null): "unverified" | "verified" | "helm" | null {
  if (f === null) return null;
  if (f.source === "helm_calculated") return "helm";
  return f.verification;
}

const NO_BROKER = "Unavailable: no Rithmic risk values for this account.";
const figureOf = (a: AccountFacts, kind: "trailingDrawdown" | "dailyLoss"): BrokerFigure | null =>
  a.broker?.[kind] ?? null;

/** A Rithmic-derived room column (ADR-0045): value, source/verification tooltip and cell badge. */
const brokerRoom = (
  id: string,
  label: string,
  category: string,
  kind: "trailingDrawdown" | "dailyLoss",
  width = 110,
): Col => ({
  id,
  label,
  category,
  definition:
    kind === "trailingDrawdown"
      ? "Rithmic account balance − Rithmic's current auto-liquidation threshold (356). Computed from Rithmic values only; marked unverified until matched against R|Trader Pro."
      : "Rithmic loss limit (305) + Rithmic day P&L (451). Computed from Rithmic values only; marked unverified until matched against R|Trader Pro.",
  kind: "money",
  width,
  minWidth: 70,
  dash: true,
  rowTitle: (a) => {
    const f = figureOf(a, kind);
    return f === null ? NO_BROKER : brokerFigureTitle(f);
  },
  badge: (a) => figureBadge(figureOf(a, kind)),
  value: (a) => figureOf(a, kind)?.valueCents ?? null,
});

/**
 * A figure from a firm rule (profit target): not Rithmic data (owner 2026-10-02). With the owner's
 * entry it is "HELM-calculated from your firm rules" (ADR-0047); without one it stays unavailable.
 */
const firmRuleColumn = (
  id: string,
  label: string,
  rule: keyof typeof FIRM_RULE_LABELS,
  width: number,
  show: "room" | "progress",
): Col => ({
  id,
  label,
  category: "Prop account",
  definition: `${FIRM_RULE_LABELS[rule]} is a firm rule, not provided by Rithmic. TradePrizm calculates this from your entry in Firm rules (target − (Rithmic balance − your starting balance)).`,
  kind: show === "room" ? "money" : "text",
  width,
  minWidth: 70,
  dash: true,
  rowTitle: (a) => {
    const t = a.broker?.targetRoom;
    if (!t || t.valueCents === null)
      return `${FIRM_RULE_LABELS[rule]}: not provided by Rithmic. ${t ? unavailableText(t.reason) : "Unavailable."}`;
    return `${t.label}: ${t.formula}.`;
  },
  badge: (a) => (a.broker?.targetRoom?.valueCents == null ? null : "helm"),
  value: (a) => {
    const t = a.broker?.targetRoom;
    if (!t || t.valueCents === null) return null;
    return show === "room" ? t.valueCents : t.progressPct === null ? null : `${t.progressPct.toFixed(1)}%`;
  },
});

export const LIMIT_WARN_PCT = 75;
export const LIMIT_DANGER_PCT = 90;
export function consumedPct(room: bigint, limit: bigint): number {
  if (limit <= 0n || room >= limit) return 0;
  return Number(((limit - room) * 100n) / limit);
}
export function limitLevel(room: bigint | null, limit: bigint | null): "warn" | "danger" | null {
  if (room === null || limit === null) return null;
  const pct = consumedPct(room, limit);
  return pct >= LIMIT_DANGER_PCT ? "danger" : pct >= LIMIT_WARN_PCT ? "warn" : null;
}

type Col = ColumnDef<AccountFacts>;
const unknown = (id: string, label: string, category = "Risk", width = 110): Col => ({
  id,
  label,
  category,
  definition: BROKER_LIMIT_UNAVAILABLE,
  kind: ["binding", "riskBasis", "riskStatus", "profitTargetPct"].includes(id) ? "text" : "money",
  width,
  minWidth: 70,
  dash: true,
  rowTitle: () => BROKER_LIMIT_UNAVAILABLE,
  value: () => null,
});
const money = (
  id: string,
  label: string,
  definition: string,
  value: Col["value"],
  field: keyof MoneyAsOf,
  kind: "money" | "pnl" = "pnl",
): Col => ({
  id,
  label,
  category: "Money",
  definition,
  value,
  kind,
  width: 110,
  minWidth: 70,
  subtotal: true,
  dash: true,
  rowTitle: (a) => {
    if (value(a) === null) return moneyUnavailableTitle(a.financeReason);
    // ADR-0056: Rithmic sends only changed fields, so the value may be from an earlier update.
    const asOf = rithmicAsOf(a.asOf?.[field]);
    return asOf === "" ? definition : `${asOf}. ${definition}`;
  },
});
export const ACCOUNT_COLUMNS: readonly Col[] = [
  {
    id: "main",
    label: "Main",
    category: "Account",
    definition: "Main account selection.",
    kind: "action",
    width: 48,
    minWidth: 40,
    unsortable: true,
    value: (a) => (a.isMain ? "main" : ""),
  },
  {
    id: "status",
    label: "Operational lock",
    category: "Account",
    definition: "Operational order/lockout state; not a broker financial figure.",
    kind: "status",
    width: 80,
    minWidth: 60,
    value: (a) => (a.status === "locked" ? "TradePrizm lock active" : null),
  },
  {
    id: "account",
    label: "Account",
    category: "Account",
    definition: "Rithmic account id or alias.",
    kind: "account",
    width: 150,
    minWidth: 80,
    locked: true,
    value: (a) => a.label,
  },
  money(
    "balance",
    "Account Balance",
    "Rithmic account balance, reported directly. This is not assumed to be cash or net liquidation.",
    (a) => a.balanceCents,
    "balance",
    "money",
  ),
  {
    id: "currency",
    label: "Currency",
    category: "Account",
    definition: "Exact Rithmic account currency; no conversion.",
    kind: "text",
    width: 80,
    minWidth: 60,
    value: (a) => a.brokerCurrency ?? null,
  },
  unknown("netLiq", "Net Liq", "Money"),
  money("realized", "RP&L", "Rithmic day closed P&L, reported directly.", (a) => a.realizedCents, "realized"),
  money(
    "openPnl",
    "UP&L",
    "Rithmic open-position P&L, reported directly; no window or gateway marks.",
    (a) => a.unrealizedCents,
    "unrealized",
  ),
  unknown("totalPnl", "Total P&L", "P&L"),
  unknown("fees", "Charges", "P&L"),
  money(
    "dayPnl",
    "Day P&L",
    "Rithmic day P&L, reported directly; not reconstructed from realized/open P&L or fees.",
    accountDayPnl,
    "dayPnl",
  ),
  money(
    "cashOnHand",
    "Cash on Hand",
    "Rithmic cash on hand, reported directly; distinct from account balance.",
    (a) => a.cashOnHandCents ?? null,
    "cashOnHand",
    "money",
  ),
  money(
    "closedPositionPnl",
    "Closed Position P&L",
    "Rithmic closed-position P&L, reported directly; distinct from day closed P&L.",
    (a) => a.closedPositionPnlCents ?? null,
    "closedPositionPnl",
  ),
  unknown("equity", "Equity"),
  {
    id: "ddThreshold",
    label: "DD Threshold",
    category: "Risk",
    definition:
      "Rithmic's current auto-liquidation threshold (356 auto_liq_threshold_current_value), reported directly.",
    kind: "money",
    width: 110,
    minWidth: 70,
    dash: true,
    rowTitle: (a) => {
      const t = a.broker?.threshold;
      if (!t) return NO_BROKER;
      return t.reason !== ""
        ? `${unavailableText(t.reason)}.`
        : `Rithmic 356 auto_liq_threshold_current_value${t.raw === null ? "" : ` ("${t.raw}")`}, reported directly.`;
    },
    value: (a) => a.broker?.threshold.valueCents ?? null,
  },
  brokerRoom("ddRoom", "DD Room", "Risk", "trailingDrawdown"),
  brokerRoom("dailyLossRoom", "Daily Loss Room", "Risk", "dailyLoss"),
  unknown("binding", "Binding Limit"),
  unknown("riskBasis", "Risk Basis"),
  unknown("riskStatus", "Broker Risk Status"),
  money("dollarDay", "Dollar (day)", "Rithmic day P&L, reported directly.", accountDayPnl, "dayPnl"),
  unknown("sinceStart", "P&L Since Start", "Prop account"),
  brokerRoom("distDrawdown", "Dist Drawdown", "Prop account", "trailingDrawdown", 104),
  brokerRoom("distAutoLiq", "Dist to Auto Liq", "Prop account", "trailingDrawdown", 104),
  unknown("drawdownAuto", "Drawdown Auto", "Prop account"),
  firmRuleColumn("distProfitTarget", "Dist to Profit Target", "profit_target", 120, "room"),
  firmRuleColumn("profitTargetPct", "Target Progress", "profit_target", 84, "progress"),
  brokerRoom("distDailyLoss", "Dist to Daily Loss Limit", "Prop account", "dailyLoss", 128),
  {
    id: "openPositions",
    label: "Open Positions",
    category: "Activity",
    definition: "Broker-observed open positions.",
    kind: "int",
    width: 96,
    minWidth: 60,
    value: (a) => a.openPositions,
  },
  {
    id: "workingOrders",
    label: "Working Orders",
    category: "Activity",
    definition: "Orders working or possibly working at the exchange.",
    kind: "int",
    width: 96,
    minWidth: 60,
    value: (a) => a.workingOrders,
  },
  {
    id: "actions",
    label: "Actions",
    category: "Actions",
    definition: "Cancel working orders and close positions at market, subject to the existing action gate.",
    kind: "action",
    width: 80,
    minWidth: 64,
    unsortable: true,
    value: () => "flatten",
  },
];

export const TRADOVATE_ACCOUNT_COLUMNS = [
  "account",
  "balance",
  "dollarDay",
  "distProfitTarget",
  "distDailyLoss",
  "distDrawdown",
  "distAutoLiq",
  "drawdownAuto",
] as const;

export const ACCOUNT_DEFAULTS = TRADOVATE_ACCOUNT_COLUMNS;

/** HELM's earlier Tradesea Accounts columns (the defaults before 2026-09-29), as a template. */
export const TRADESEA_ACCOUNT_COLUMNS = [
  "main",
  "riskBasis",
  "status",
  "account",
  "balance",
  "realized",
  "openPnl",
  "fees",
  "openPositions",
  "workingOrders",
  "actions",
] as const;

export const ACCOUNTS_TABLE: TableDef<AccountFacts> = {
  id: "accounts",
  label: "Accounts",
  columns: ACCOUNT_COLUMNS,
  defaults: ACCOUNT_DEFAULTS,
  defaultTemplate: "builtin:tradovate",
  // "Dollar Total" had no Rithmic source and always showed "—"; the Rithmic balance takes its place
  // (owner, 2026-10-05).
  replacedColumns: { dollarTotal: "balance" },
  builtIns: [
    { id: "builtin:tradovate", name: "Tradovate", columns: [...TRADOVATE_ACCOUNT_COLUMNS] },
    {
      id: "builtin:prop",
      name: "Prop evaluation",
      columns: [
        "account",
        "balance",
        "dollarDay",
        "distProfitTarget",
        "profitTargetPct",
        "distDailyLoss",
        "distDrawdown",
        "distAutoLiq",
        "drawdownAuto",
        "riskStatus",
        "riskBasis",
      ],
    },
    {
      id: "builtin:tradesea",
      name: "Tradesea (TradePrizm before 2026-09-29)",
      columns: [...TRADESEA_ACCOUNT_COLUMNS],
    },
  ],
};
